// Head manager for the SPA: keeps <title>, meta, canonical link and JSON-LD
// in sync with the current route. Tags created here are stamped with
// data-ds-seo so re-renders update them in place; the static tags CRA ships
// in index.html (description, og:*) are adopted and updated rather than
// duplicated.

const ensureTag = (selector, create) => {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = create();
        element.setAttribute('data-ds-seo', '');
        document.head.appendChild(element);
    }
    return element;
};

const upsertMeta = (attr, key, content) => {
    const selector = `meta[${attr}="${key}"]`;
    if (!content) {
        const existing = document.head.querySelector(selector);
        if (existing) {
            existing.remove();
        }
        return;
    }
    const element = ensureTag(selector, () => {
        const meta = document.createElement('meta');
        meta.setAttribute(attr, key);
        return meta;
    });
    element.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
    const selector = `link[rel="${rel}"][data-ds-seo]`;
    if (!href) {
        const existing = document.head.querySelector(selector);
        if (existing) {
            existing.remove();
        }
        return;
    }
    const element = ensureTag(selector, () => {
        const link = document.createElement('link');
        link.setAttribute('rel', rel);
        return link;
    });
    element.setAttribute('href', href);
};

// One <script> per schema entity — the conventional JSON-LD shape. Some
// consumers (browser SEO extensions, naive crawlers) assume each script
// holds a single object and crash on an array.
const setJsonLd = (data) => {
    document.head
        .querySelectorAll('script[type="application/ld+json"][data-ds-seo]')
        .forEach((element) => element.remove());
    const entries = !data ? [] : (Array.isArray(data) ? data : [data]);
    entries.forEach((entry) => {
        const element = document.createElement('script');
        element.type = 'application/ld+json';
        element.setAttribute('data-ds-seo', '');
        element.textContent = JSON.stringify(entry);
        document.head.appendChild(element);
    });
};

// Applies a fully-resolved meta description of the current page to the head.
export const applySeoTags = ({
    title,
    description,
    keywords,
    canonical,
    noindex,
    siteName,
    image,
    type,
    twitterHandle,
    googleSiteVerification,
    bingSiteVerification,
    jsonLd,
}) => {
    if (title) {
        document.title = title;
    }

    upsertMeta('name', 'description', description || '');
    upsertMeta('name', 'keywords', Array.isArray(keywords) && keywords.length ? keywords.join(', ') : '');
    upsertMeta('name', 'robots', noindex ? 'noindex, follow' : '');
    upsertMeta('name', 'google-site-verification', googleSiteVerification || '');
    upsertMeta('name', 'msvalidate.01', bingSiteVerification || '');

    upsertLink('canonical', noindex ? '' : canonical || '');

    upsertMeta('property', 'og:site_name', siteName || '');
    upsertMeta('property', 'og:title', title || '');
    upsertMeta('property', 'og:description', description || '');
    upsertMeta('property', 'og:type', type || 'website');
    upsertMeta('property', 'og:url', canonical || '');
    upsertMeta('property', 'og:image', image || '');

    upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:site', twitterHandle || '');
    upsertMeta('name', 'twitter:title', title || '');
    upsertMeta('name', 'twitter:description', description || '');
    upsertMeta('name', 'twitter:image', image || '');

    setJsonLd(jsonLd);
};

// --------------------------------------------------------------------------
// Shared helpers
// --------------------------------------------------------------------------

export const stripHtml = (html) => {
    if (typeof html !== 'string' || !html) {
        return '';
    }
    if (html.indexOf('<') === -1) {
        return html.trim();
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

// Truncates on a word boundary; meta descriptions should stay under ~160.
export const truncateText = (text, maxLength = 160) => {
    const value = String(text || '').trim();
    if (value.length <= maxLength) {
        return value;
    }
    const cut = value.slice(0, maxLength - 1);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trim()}…`;
};

// decodeURIComponent that never throws: malformed percent sequences (a
// crawler probing /tag/100%) fall back to the raw value, matching React
// Router's own lenient param decoding.
export const safeDecode = (value) => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

// '/about/' -> '/about'; strips query/hash; '' for invalid. Each segment is
// re-encoded so '/tag/new york' and '/tag/new%20york' normalize identically.
// Mirrors the backend's normalizePath so override lookups agree.
export const normalizePath = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    let path = value.trim().split(/[?#]/)[0];
    if (!path) {
        return '';
    }
    if (!path.startsWith('/')) {
        path = `/${path}`;
    }
    if (path.length > 1) {
        path = path.replace(/\/+$/, '') || '/';
    }
    return path
        .split('/')
        .map((segment) => encodeURIComponent(safeDecode(segment)))
        .join('/');
};

export const absoluteUrl = (siteUrl, pathOrUrl) => {
    const value = String(pathOrUrl || '').trim();
    if (!value) {
        return '';
    }
    if (/^[a-z][a-z0-9+\-.]*:\/\//i.test(value)) {
        return value;
    }
    const base = String(siteUrl || '').replace(/\/+$/, '');
    return `${base}${value.startsWith('/') ? value : `/${value}`}`;
};

// Applies the settings titleTemplate ('%s - The Discerner') to a bare title.
export const applyTitleTemplate = (template, title) => {
    if (!title) {
        return '';
    }
    if (!template || template.indexOf('%s') === -1) {
        return title;
    }
    // Function replacer so titles containing $-sequences ('$&', '$$') are
    // inserted literally instead of being expanded by String.replace.
    return template.replace('%s', () => title);
};
